import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Users,
  Search,
  Eye,
  Send,
  CheckCircle,
  Power,
  RefreshCw,
  Filter,
  Tag,
  X
} from 'lucide-react';

import apiService from '../../services/api';
// import TagSelector from './TagSelector'; // Removido - não utilizado

const CampaignTab = ({ onCampaignStart, onError, onSuccess, loading, setLoading }) => {
  // Removida a importação não utilizada de isDarkMode
  
  // Estados locais
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [messageTemplate, setMessageTemplate] = useState('Olá [NOME]! 🌟 Temos uma oferta especial para você! 📞 Entre em contato conosco para mais detalhes.');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sendInterval, setSendInterval] = useState(5);
  const [lastLoadTime, setLastLoadTime] = useState(null);
  
  // Estados para filtros
  // const [statusFilter, setStatusFilter] = useState(''); // Removido - não utilizado
  // const [selectedTagsFilter, setSelectedTagsFilter] = useState([]); // Removido - não utilizado
  const [showFilters, setShowFilters] = useState(false);
  const [lastContactFilter, setLastContactFilter] = useState('');
  
  // Estados para ações
  const [startingCampaign, setStartingCampaign] = useState(false);
  // const [importingContacts, setImportingContacts] = useState(false); // Removido - não utilizado
  
  // Refs para evitar dependências que causam loops
  const loadingRef = useRef(loading);
  const lastLoadTimeRef = useRef(lastLoadTime);
  
  // Atualizar refs quando os valores mudarem
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  
  useEffect(() => {
    lastLoadTimeRef.current = lastLoadTime;
  }, [lastLoadTime]);
  
  // Cache duration
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  
  // targetDbName removido - não utilizado

  // Estatísticas calculadas
  const stats = useMemo(() => {
    const totalClients = contacts?.length || 0;
    const available = contacts?.filter(c => c?.whatsappStatus === 'available')?.length || 0;
    const selected = selectedContacts?.length || 0;
    const sent = contacts?.filter(c => c?.status === 'sent')?.length || 0;
    
    return {
      totalClients,
      available,
      selected,
      sent
    };
  }, [contacts, selectedContacts]);

  // Carregar contatos
  const loadContacts = useCallback(async (forceReload = false, searchTerm = '', statusFilterParam = '', tagsFilterParam = []) => {
    // Verificar cache se não for reload forçado
    if (!forceReload && lastLoadTimeRef.current) {
      const timeSinceLastLoad = Date.now() - lastLoadTimeRef.current;
      if (timeSinceLastLoad < CACHE_DURATION) {
        // Dados ainda são válidos, usar cache
        return;
      }
    }

    // Evitar múltiplas requisições simultâneas
    if (loadingRef.current) {
      
      return;
    }

    try {
      setLoading(true);

      // Garantir que tagsFilterParam seja sempre um array
      const tagsArray = Array.isArray(tagsFilterParam) ? tagsFilterParam : [];

      // Carregar todos os contatos usando paginação
      let allContacts = [];
      let currentPage = 1;
      let hasMorePages = true;
      const pageLimit = 1000; // Limite por página

      while (hasMorePages) {
        const params = new URLSearchParams({
          page: currentPage,
          limit: pageLimit,
          search: searchTerm,
          status: statusFilterParam,
          tags: tagsArray.join(',')
        });

        const response = await apiService.request(`/crm/contacts?${params}`);
        
        if (response.success && response.data && Array.isArray(response.data.contacts)) {
          allContacts = [...allContacts, ...response.data.contacts];
          
          // Verificar se há mais páginas
          const { page, pages } = response.data.pagination;
          hasMorePages = page < pages;
          currentPage++;
          

        } else {
          hasMorePages = false;
        }
      }
      
      if (allContacts.length > 0) {
        const contactsData = allContacts;
        

        
        // Processar contatos para o formato esperado pela campanha
        const processedContacts = contactsData.map(contact => ({
          id: contact.id,
          name: contact.nome || 'Nome não informado',
          phone: contact.number || 'Telefone não informado',
          email: contact.email || '',
          status: contact.status || 'ativo',
          lastContact: contact.original_date || contact.created_at || new Date().toISOString().split('T')[0],
          whatsappStatus: contact.status === 'ativo' ? 'available' : 'unavailable',
          whatsappId: contact.number?.replace(/\D/g, ''),
          profilePicUrl: null,
          tags: contact.tags || [],
          blocked_at: contact.blocked_at,
          blocked_reason: contact.blocked_reason
        }));
        
        setContacts(processedContacts);
        setLastLoadTime(Date.now());
      } else {

        setContacts([]);
      }
    } catch (error) {

      onError('Erro ao carregar contatos: ' + error.message);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [CACHE_DURATION, setLoading, onError]); // Removidas lastLoadTime e loading para evitar loop

  // Função para importar contatos foi removida (não utilizada)

  // Função para selecionar/deselecionar contatos
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  // Função para limpar seleção
  const clearSelection = () => {
    setSelectedContacts([]);
  };



  // Função para aplicar filtros de último contato
  const applyLastContactFilter = useCallback((contactsList) => {
    if (!lastContactFilter) return contactsList;
    
    const daysAgo = parseInt(lastContactFilter);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Final do dia de hoje
    
    const targetDate = new Date(today);
    targetDate.setDate(targetDate.getDate() - daysAgo);
    targetDate.setHours(0, 0, 0, 0); // Início do dia alvo
    
    return contactsList.filter(contact => {
      if (!contact.lastContact) return false;
      
      const contactDate = new Date(contact.lastContact);
      
      // Filtrar contatos que tiveram último contato há mais de X dias
      return contactDate < targetDate;
    });
  }, [lastContactFilter]);



  // Função para iniciar campanha
  const startCampaign = async () => {
    if (selectedContacts.length === 0) {
      onError('Por favor, selecione pelo menos um contato para iniciar a campanha.');
      return;
    }

    if (!messageTemplate.trim()) {
      onError('Por favor, insira uma mensagem para a campanha.');
      return;
    }

    setStartingCampaign(true);
    try {
      
      
      // Upload da imagem primeiro, se selecionada
      let imageUrl = null;
      if (selectedImage) {
        
        try {
          const uploadResponse = await apiService.uploadWhatsAppImage(selectedImage);
          if (uploadResponse.success) {
            imageUrl = uploadResponse.imageUrl;
            
          } else {
            throw new Error(uploadResponse.error || 'Falha no upload da imagem');
          }
        } catch (uploadError) {
          
          onError('Erro ao fazer upload da imagem: ' + uploadError.message);
          return;
        }
      }
      
      const selectedContactsData = selectedContacts.map(contactId => {
        const contact = contacts.find(c => c.id === contactId);
        return {
          id: contact.id,
          name: contact.name,
          phone: contact.phone,
          whatsappId: contact.whatsappId || contact.phone.replace(/\D/g, '')
        };
      });

      const campaignData = {
        contacts: selectedContactsData,
        message: messageTemplate,
        interval: sendInterval,
        mediaUrl: imageUrl // Agora é uma URL string, não File object
      };

      const response = await apiService.sendWhatsAppBulkMessages(campaignData);
      
      if (response.success) {
        // Notificação removida
        
        // Notificar o dashboard para mostrar o progresso
        onCampaignStart({
          id: response.campaignId || Date.now(),
          contacts: selectedContactsData,
          message: messageTemplate,
          interval: sendInterval,
          totalContacts: selectedContacts.length,
          sentCount: 0,
          status: 'active'
        });
        
        // Limpar seleção após iniciar campanha
        clearSelection();
      } else {
        throw new Error(response.message || 'Falha ao iniciar campanha');
      }
    } catch (error) {
      
      onError('Erro ao iniciar campanha: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setStartingCampaign(false);
    }
  };

  // Filtrar contatos baseado na busca e filtros adicionais
  const filteredContacts = useMemo(() => {
    let result = contacts;
    
    // Excluir contatos com status 'sent' da seleção para evitar re-envio
    result = result.filter(contact => contact.status !== 'sent');
    
    // Aplicar filtro de último contato
    result = applyLastContactFilter(result);
    
    return result;
  }, [contacts, applyLastContactFilter]);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedSearchTerm !== searchTerm) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // Recarregar quando filtros mudarem
  useEffect(() => {
    if (hasLoadedInitially.current) {
      loadContacts(true, debouncedSearchTerm, '', []);
    }
  }, [debouncedSearchTerm, loadContacts]); // Incluída dependência loadContacts

  // Ref para controlar se já carregou inicialmente
  const hasLoadedInitially = useRef(false);

  // Carregar contatos ao montar o componente
  useEffect(() => {
    if (!hasLoadedInitially.current) {
      hasLoadedInitially.current = true;
      loadContacts(false, '', '', []); // Usar valores fixos para carregamento inicial - tagsFilterParam deve ser array
    }
  }, [loadContacts]); // Incluída dependência loadContacts

  return (
    <div className="space-y-6">
      {/* Informações sobre Importação de Contatos - Movido para cima */}
      {filteredContacts.length === 0 && !loading && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Nenhum contato encontrado
              </h3>
              <p className="text-blue-800 dark:text-blue-200 mb-3">
                Para começar a usar o CRM do WhatsApp, você precisa importar seus contatos do WhatsApp para o banco de dados.
              </p>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="mb-2"><strong>Como funciona a importação:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Busca todos os contatos do seu WhatsApp conectado</li>
                  <li>Salva os contatos na tabela do banco de dados do seu sistema</li>
                  <li>Evita duplicatas - só adiciona contatos novos</li>
                  <li>Formata números de telefone automaticamente</li>
                  <li>Mostra quantos contatos foram importados e quantos já existiam</li>
                </ul>
                <p className="mt-3 font-medium">
                  Clique no botão "Importar Contatos" na aba Contatos para começar.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total de Clientes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalClients}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Disponíveis</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.available}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Eye className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Selecionados</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.selected}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Send className="w-8 h-8 text-green-600 dark:text-green-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Já Enviados</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.sent}</p>
            </div>
          </div>
        </div>
      </div>



      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Template da Mensagem */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Template da Mensagem</h2>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mensagem
                </label>
                <textarea
                  value={messageTemplate}
                  onChange={(e) => setMessageTemplate(e.target.value)}
                  placeholder="Olá [NOME]! 🌟 Temos uma oferta especial para você! 📞 Entre em contato conosco para mais detalhes."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  rows={4}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use [NOME] para personalizar com o nome do cliente
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Selecione uma imagem para acompanhar a mensagem (opcional)
                </label>
                <label className="inline-flex items-center px-4 py-2 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors duration-200">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Selecionar Imagem
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedImage(file);
                        const reader = new FileReader();
                        reader.onload = (e) => setImagePreview(e.target.result);
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                  />
                </label>
                
                {imagePreview && (
                  <div className="mt-4">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="max-w-xs max-h-48 rounded-lg border border-gray-300 dark:border-gray-600"
                    />
                    <button
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                      className="mt-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
                    >
                      Remover imagem
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Configuração de Envio
                </label>
                <select
                  value={sendInterval}
                  onChange={(e) => setSendInterval(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value={5}>Intervalo de 5 minutos</option>
                  <option value={10}>Intervalo de 10 minutos</option>
                </select>
              </div>



              {/* Botão de Iniciar Campanha */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <button
                  onClick={startCampaign}
                  disabled={startingCampaign || selectedContacts.length === 0 || !messageTemplate.trim()}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {startingCampaign ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Iniciando Campanha...</span>
                    </>
                  ) : (
                    <>
                      <Power className="w-5 h-5" />
                      <span>Iniciar Campanha ({selectedContacts.length} contatos)</span>
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                  Inicia o envio da mensagem para todos os contatos selecionados
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de contatos */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Selecionar Destinatários</h2>
            </div>

            {/* Busca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar por nome ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Botão de filtros */}
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros
                {lastContactFilter && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full text-xs">
                    1
                  </span>
                )}
              </button>

              {lastContactFilter && (
                <button
                  onClick={() => {
                    setLastContactFilter('');
                  }}
                  className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Limpar filtros
                </button>
              )}
            </div>

            {/* Filtros expandidos */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mt-3">
                {/* Pesquisar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pesquisar
                  </label>
                  <button
                    onClick={() => loadContacts(true, searchTerm, '', [])}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Carregando...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        <span>Pesquisar</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Filtro por último contato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Último Contato
                  </label>
                  <select
                    value={lastContactFilter}
                    onChange={(e) => setLastContactFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  >
                    <option value="">Qualquer data</option>
                    <option value="15">Mais de 15 dias</option>
                    <option value="30">Mais de 30 dias</option>
                  </select>
                </div>

                {/* Seleção de contatos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Seleção Rápida
                  </label>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => {
                        const contactsToSelect = filteredContacts.slice(0, 25).map(contact => contact.id);
                        setSelectedContacts(contactsToSelect);
                      }}
                      disabled={filteredContacts.length === 0}
                      className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Selecionar 25
                    </button>
                    <button
                      onClick={() => {
                        const contactsToSelect = filteredContacts.slice(0, 50).map(contact => contact.id);
                        setSelectedContacts(contactsToSelect);
                      }}
                      disabled={filteredContacts.length === 0}
                      className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Selecionar 50
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Ações em lote */}
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={clearSelection}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                Limpar Seleção
              </button>
              
              <button
                onClick={() => loadContacts(true, debouncedSearchTerm, '', [])}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Carregando...' : 'Recarregar'}
              </button>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <div className="text-gray-500 dark:text-gray-400">Carregando contatos...</div>
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Nenhum contato encontrado</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Mostrando {filteredContacts.length} contatos
                  {debouncedSearchTerm && ` para "${debouncedSearchTerm}"`}
                </div>
                
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {filteredContacts.map((contact) => {
                    if (!contact || !contact.id) return null;
                    
                    return (
                      <div
                        key={contact.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedContacts.includes(contact.id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => toggleContactSelection(contact.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <input
                              type="checkbox"
                              checked={selectedContacts.includes(contact.id)}
                              onChange={() => toggleContactSelection(contact.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            
                            {/* Avatar placeholder */}
                            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {contact.name || 'Nome não informado'}
                                </p>
                                
                                {/* Status indicator */}
                                {contact.status === 'ativo' && (
                                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                )}
                                {contact.status === 'ignorado' && (
                                  <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                                )}
                                {contact.status === 'pausado' && (
                                  <div className="w-4 h-4 bg-yellow-600 dark:bg-yellow-400 rounded-full"></div>
                                )}
                              </div>
                              
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {contact.phone || 'Telefone não informado'}
                              </p>
                              
                              {/* Tags */}
                              {contact.tags && contact.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {contact.tags.slice(0, 3).map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                                    >
                                      <Tag className="w-2 h-2 mr-1" />
                                      {tag}
                                    </span>
                                  ))}
                                  {contact.tags.length > 3 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      +{contact.tags.length - 3} mais
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {/* Status badge */}
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              contact.status === 'ativo' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : contact.status === 'ignorado'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                            }`}>
                              {contact.status === 'ativo' ? 'Ativo' : 
                               contact.status === 'ignorado' ? 'Ignorado' : 'Pausado'}
                            </span>
                            
                            {contact.phone && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const phoneNumber = contact.phone.replace(/\D/g, '');
                                  window.open(`https://wa.me/${phoneNumber}`, '_blank');
                                }}
                                className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                                title="Abrir WhatsApp"
                              >
                                <svg 
                                  className="w-4 h-4" 
                                  fill="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.515z"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignTab;